import { useState } from 'react';
import {
  Dialog,
  Flex,
  Button,
  IconButton,
  Icon,
  Text,
  TextField,
  Select,
  Skeleton,
} from '@scale/scaleui-radix';
import { faClose } from '@fortawesome/pro-regular-svg-icons';
import {
  IAuditErrorCategoryInput,
  getSerializedErrorCategoryString,
} from '@scale/shared/types/auditErrorCategories';
import useErrorCategoryOptions, {
  getDefaultErrorCategory,
} from 'react-scale/corp/views/errorCategories/useErrorCategoryOptions';
import {
  useProjectFieldIds,
  useAddProjectSpecificErrorCategory,
} from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';
import { toast } from 'react-toastify';

// partially copied from errorCategories/ErrorCategoriesEditor.tsx

export const AddErrorCategoryModal = ({
  open,
  setOpen,
  addErrorCategory,
  projectId,
  userEmail,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  addErrorCategory: (errorCategory: string) => void;
  projectId: string;
  userEmail: string;
}) => {
  const [newCategory, setNewCategory] = useState<IAuditErrorCategoryInput>(
    getDefaultErrorCategory(projectId, userEmail),
  );

  const { data: fieldIds, isLoading: isLoadingFieldIds } = useProjectFieldIds(projectId);

  const addErrorCategoryMutation = useAddProjectSpecificErrorCategory();

  const { fieldIdOptions, distinctDimensions, isLoading } = useErrorCategoryOptions(
    projectId,
    fieldIds ?? [],
  );

  const isLoadingCanAddNewCategory = isLoading || isLoadingFieldIds;

  const handleSave = async () => {
    try {
      await addErrorCategoryMutation.mutateAsync({ projectId, category: newCategory });
      addErrorCategory(getSerializedErrorCategoryString(newCategory));
      setNewCategory(getDefaultErrorCategory(projectId, userEmail));
      toast.success('Error category added successfully');
    } catch (error) {
      toast(`Failed to add error category: ${error.message}`, {
        type: 'error',
        autoClose: 5000,
        position: 'top-right',
      });
    }
    setOpen(false);
  };
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Content>
        <Flex justify="between" mb="3">
          <Dialog.Title>Add Error Category</Dialog.Title>
          <Dialog.Close>
            <IconButton variant="ghost" color="neutral">
              <Icon icon={faClose} label="Close" />
            </IconButton>
          </Dialog.Close>
        </Flex>
        <Flex direction="column" gap="2">
          <Flex direction="row" gap="2" justify="between" align="center">
            <Text size="2">Taxonomy</Text>
            {isLoadingCanAddNewCategory ? (
              <Skeleton></Skeleton>
            ) : (
              <Select.Root
                value={newCategory.fieldId}
                onValueChange={value => setNewCategory({ ...newCategory, fieldId: value })}
              >
                <Select.Trigger style={{ width: '200px' }} />
                <Select.Content>
                  {fieldIdOptions.map(fieldId => (
                    <Select.Item key={fieldId.id} value={fieldId.id}>
                      {fieldId.name}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            )}
          </Flex>
          <Flex direction="row" gap="2" justify="between" align="center">
            <Text size="2">Dimension</Text>
            {isLoadingCanAddNewCategory ? (
              <Skeleton></Skeleton>
            ) : (
              <Select.Root
                value={newCategory.dimension}
                onValueChange={value => setNewCategory({ ...newCategory, dimension: value })}
              >
                <Select.Trigger style={{ width: '200px' }} />
                <Select.Content>
                  {distinctDimensions.map(dimension => (
                    <Select.Item key={dimension} value={dimension}>
                      {dimension}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            )}
          </Flex>
          <Flex direction="row" gap="2" justify="between" align="center">
            <Text size="2">Error Category</Text>
            <TextField.Root
              style={{ width: '200px' }}
              value={newCategory.errorCategory}
              onChange={e => setNewCategory({ ...newCategory, errorCategory: e.target.value })}
            />
          </Flex>
          <Flex direction="row" gap="2" justify="between" align="center">
            <Text size="2">Description</Text>
            <TextField.Root
              style={{ width: '200px' }}
              value={newCategory.description}
              onChange={e => setNewCategory({ ...newCategory, description: e.target.value })}
            />
          </Flex>
          <Flex direction="row" gap="2" justify="between" align="center">
            <Text size="2">Max Score</Text>
            <TextField.Root
              style={{ width: '200px' }}
              type="number"
              value={newCategory.maxScore}
              onChange={e => setNewCategory({ ...newCategory, maxScore: Number(e.target.value) })}
            />
          </Flex>
          <Flex direction="row" gap="2" justify="end" align="center">
            <Button onClick={handleSave} loading={addErrorCategoryMutation.isLoading}>
              Add
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};
